import React from "react";
import PropTypes from "prop-types";
import cname from "./style.module.scss";
import { PlungerTooltip as Tooltip } from "../MicroPipetteImages";
import { HamburgerIcon } from "../../../Icons";
import { Image } from "@learningblocks/shared-components/lib/Image";
import {
  P2,
  P20,
  P200,
  P1000,
  PLUNGER_STATE,
  PLUNGER_TOOLTIP_TYPE,
} from "../MicroPipette.constants";

let intervalSecondState = null;
const DURATION_TO_SECOND_STOP = 1200;

export function PlungerTooltip(props) {
  const [plungerState, setPlungetState] = React.useState(PLUNGER_STATE.RELEASE);
  const [isKeyPressed, setIsKeyPressed] = React.useState(false);
  const hamburgerButtonRef = React.useRef(null);

  // Pass hamburger button ref to parent when button is rendered
  React.useEffect(() => {
    if (hamburgerButtonRef.current && props.onSetHamburgerButtonRef) {
      props.onSetHamburgerButtonRef(hamburgerButtonRef);
    }
  }, [props.onSetHamburgerButtonRef]);

  const isMixerTooltip = () =>
    props.plungerTooltip === PLUNGER_TOOLTIP_TYPE.MIXER;

  const getPassiveTooltipType = () => {
    if (isMixerTooltip()) {
      return PLUNGER_TOOLTIP_TYPE.STOPPER;
    } else {
      return PLUNGER_TOOLTIP_TYPE.MIXER;
    }
  };

  const clearWait = () => {
    if (intervalSecondState) {
      clearTimeout(intervalSecondState);
      intervalSecondState = null;
    }
  };

  const onMouseDown = () => {
    if (isMixerTooltip()) {
      setPlungetState(PLUNGER_STATE.MIXING);
    } else {
      clearWait();
      setPlungetState(PLUNGER_STATE.FIRST_STOP);
      intervalSecondState = setTimeout(() => {
        setPlungetState(PLUNGER_STATE.SECOND_STOP);
      }, DURATION_TO_SECOND_STOP);
    }
  };

  const onMouseUp = React.useCallback(() => {
    clearWait();
    switch (plungerState) {
      case PLUNGER_STATE.MIXING:
        props.onMix(props.elementId);
        break;
      case PLUNGER_STATE.FIRST_STOP:
        props.onFirstStopper(props.elementId);
        break;
      case PLUNGER_STATE.SECOND_STOP:
        props.onSecondStopper(props.elementId);
        break;
      default:
        break;
    }
    setPlungetState(PLUNGER_STATE.RELEASE);
  }, [plungerState, props]);

  React.useEffect(() => {
    const handleBlur = () => {
      if (isKeyPressed) {
        setIsKeyPressed(false);
        onMouseUp();
      }
    };

    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("blur", handleBlur);
    };
  }, [isKeyPressed, onMouseUp]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!isKeyPressed) {
        setIsKeyPressed(true);
        onMouseDown();
      }
    }
  };

  const onKeyUp = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (isKeyPressed) {
        setIsKeyPressed(false);
        onMouseUp();
      }
    }
  };

  const onPassiveclick = () => {
    let param = PLUNGER_TOOLTIP_TYPE.MIXER;
    if (isMixerTooltip()) {
      param = PLUNGER_TOOLTIP_TYPE.STOPPER;
    }
    props.togglePlungerTooltip(param);
  };

  return (
    <>
      {props.showMoveMenu && (
        <div className={cname.hamburgerMenuButton}>
          <div
            ref={hamburgerButtonRef}
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              props.openMoveMenu();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                props.openMoveMenu();
              }
            }}
            className={cname.hamburgerButton}
            aria-label="Open move menu"
          >
            <Image src={HamburgerIcon} alt="Move menu" />
          </div>
        </div>
      )}
      <div className={cname.passive}>
        <Tooltip
          type={props.type}
          plungerState={PLUNGER_STATE.RELEASE}
          tooltipType={getPassiveTooltipType()}
        />
      </div>
      <div className={cname.active}>
        <Tooltip
          type={props.type}
          plungerState={plungerState}
          tooltipType={props.plungerTooltip}
        />
      </div>
      <span
        role="button"
        tabIndex={0}
        className={cname.activeClickArea}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      />
      <span
        className={cname[`passive_${getPassiveTooltipType()}`]}
        onClick={onPassiveclick}
        role="button"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            props.onClick;
          }
        }}
      />
    </>
  );
}

PlungerTooltip.propTypes = {
  /**
   * Type of MicroPipette
   */
  type: PropTypes.oneOf([P2, P20, P200, P1000]),
  /**
   * Unique Id of element Pipette is interacting with
   */
  elementId: PropTypes.number,
  /**
   *  Plunget Tooltip set as current
   */
  plungerTooltip: PropTypes.oneOf([
    PLUNGER_TOOLTIP_TYPE.MIXER,
    PLUNGER_TOOLTIP_TYPE.STOPPER,
  ]),
  /**
   * Funtion to toggle currentPlungerTooltip
   */
  togglePlungerTooltip: PropTypes.func,
  /**
   * Callback function for First Stopper Press
   */
  onFirstStopper: PropTypes.func,
  /**
   * Callback function for Second Stopper Press
   */
  onSecondStopper: PropTypes.func,
  /**
   * Callback function for Mixer Operation
   */
  onMix: PropTypes.func,
  /**
   * showMoveMenu to check moveMenu to display or not
   */
  showMoveMenu: PropTypes.bool,
  /**
   * Callback to open move menu modal
   */
  openMoveMenu: PropTypes.func,
  /**
   * Callback to set hamburger button ref for focus management
   */
  onSetHamburgerButtonRef: PropTypes.func,
};

PlungerTooltip.defaultProps = {};
